import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { SPECIALIST_INFO } from "@/graphql-client/fragments";
import { Specialist, SpecialistOption } from "@/types/api";

export const QUERY_SPECIALISTS = gql`
  query QuerySpecialistsDropdown {
    specialists {
      ...SpecialistInfo
    }
  }
  ${SPECIALIST_INFO}
`;

export const useSpecialistDropdown = () => {
  const { data, loading, error } = useQuery<{ specialists: Specialist[] }>(QUERY_SPECIALISTS);

  const { options, prefixMapping } = useMemo(() => {
    if (!data?.specialists) {
      return { options: [], prefixMapping: {} };
    }

    const allSpecialists = data.specialists.map((specialist: Specialist) => {
      const displayName = specialist.firstName;

      let prefix;
      if (specialist.lastName) {
        prefix = `${specialist.firstName.charAt(0)}${specialist.lastName.charAt(0)}`.toUpperCase();
      } else {
        prefix = specialist.firstName.substring(0, 2).toUpperCase();
      }

      return {
        value: specialist.id,
        label: displayName,
        firstName: specialist.firstName,
        lastName: specialist.lastName || "",
        fullName: specialist.lastName
          ? `${specialist.firstName} ${specialist.lastName}`
          : specialist.firstName,
        prefix,
        email: specialist.email,
      };
    });

    const specialistPrefixMap: Record<number, string> = {};
    allSpecialists.forEach((specialist: SpecialistOption) => {
      specialistPrefixMap[specialist.value] = specialist.prefix;
    });

    const sortedSpecialists = [...allSpecialists].sort((a, b) => {
      return a.firstName.localeCompare(b.firstName);
    });

    return {
      options: sortedSpecialists,
      prefixMapping: specialistPrefixMap,
    };
  }, [data]);

  return {
    options,
    prefixMapping,
    loading,
    error,
  };
};
